/**
 * Owner picker opened from a pipeline card's footer.
 *
 * Replaces the read-only owner badge with a reassignment surface when the
 * viewer is permitted to write the owner association: click opens a
 * dropdown of org members (plus an "Unassigned" clear option) instead of
 * navigating to the owner's record. When the viewer lacks write access —
 * or the owner association's schema doesn't allow it — this renders the
 * same read-only, click-to-view badge the card always showed.
 *
 * `PipelineCardParts.tsx` carries no permission logic today because it is
 * pure display; this component is the first write surface on the pipeline
 * card footer, so it owns the gate itself rather than assuming the caller
 * checked first.
 */

import { useDndMonitor } from '@dnd-kit/core';
import { SearchLg } from '@untitledui/icons';
import { useCallback, useMemo, useState } from 'react';
import type { Key } from 'react-aria-components';
import { Button as AriaButton } from 'react-aria-components';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useOrgUsers } from '@/components/csv-import/hooks/useOrgUsers';
import { useOrg } from '@/contexts/OrgContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Avatar } from '@/lib/base/avatar/avatar';
import { Input } from '@/lib/base/forms/input/input';
import { Dropdown } from '@/lib/base/overlays/dropdown';
import { Tooltip } from '@/lib/base/tooltip/tooltip';
import { useSlideoutStack } from '@/lib/crm/hooks/useSlideoutStack';
import { getAvatarInitials } from '@/lib/crm/utils/avatar';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';
import { cx } from '@/utils/cx';

import { getUserFriendlyEventError } from '../../core/errors';
import type { AssociationType } from '../../core/SchemaClient';
import { getEntityTypeConfig } from '../../record/registry/entityTypeRegistry';
import { isAssociationValueFrozen } from '../../utils/associationEditability';
import { useAssignSingleAssociation } from '../hooks/useDealStageMove';
import { useDndContainment } from '../utils/dndContainment';
import { extractStringProperty } from './PipelineCardParts';

/** Sentinel dropdown key for clearing the owner association. */
const UNASSIGNED_KEY = '__unassigned__';

/** Cap on the unfiltered owner list rendered into the dropdown — orgs with
 *  hundreds of members would otherwise render that many DOM nodes on every
 *  open. A non-empty search query narrows by name/email instead of capping. */
const OWNER_LIST_CAP = 50;

/** Minimum WCAG 2.2 SC 2.5.8 touch-target size, in Tailwind `p-*` steps,
 *  around the `xxs` (16px) unowned-state avatar: (24 - 16) / 2 = 4px. */
const UNOWNED_HIT_AREA_CLASSES = 'p-1 -m-1 rounded-full';

export interface CardOwnerPickerProps {
  /** The pipeline card entity whose owner association is being edited. */
  item: CrmObject;
  /** Currently associated owner, if any. */
  owner?: CrmObject;
  /**
   * Owner association type name, e.g. `'ahoy_deal_to_owner'`. An empty
   * string (cold schema cache) disables the picker — mirrors
   * `useAssignSingleAssociation`'s own no-op guard.
   */
  associationTypeName: string;
  /**
   * Schema for the owner association type — drives the permission and
   * frozen-value gates. `undefined` while association types are still
   * loading renders the read-only badge, the same as a failed gate.
   */
  associationType?: AssociationType;
}

function getOwnerDisplayName(owner: CrmObject): string | undefined {
  return (
    getPropertyValue<string>(owner.properties?.ahoy_display_name) || undefined
  );
}

// ---------------------------------------------------------------------------
// ReadOnlyOwnerBadge — the pre-existing display-only treatment, used when the
// viewer can't write the association. Click still opens the owner's record.
// ---------------------------------------------------------------------------

function ReadOnlyOwnerBadge({ owner }: { owner: CrmObject }) {
  const ownerName = getOwnerDisplayName(owner);
  const ownerAvatarUrl = extractStringProperty(owner, 'ahoy_avatar_url');
  const ownerInitials = useMemo(
    () => (ownerName ? getAvatarInitials(ownerName) : undefined),
    [ownerName]
  );

  // App-level click (pipeline board): opening the owner REPLACES the open
  // slideouts — only clicks from inside a slideout stack.
  const { openSlideout } = useSlideoutStack();
  const ownerEntityId = owner.entity_id;
  const containment = useDndContainment();
  const handleOwnerClick = useCallback(
    (e: React.MouseEvent) => {
      containment.onClick(e);
      if (ownerEntityId) {
        openSlideout(ownerEntityId);
      }
    },
    [containment, ownerEntityId, openSlideout]
  );

  if (!ownerName) {
    // Placeholder keeps the parent flex layout's right-aligned slot right.
    return <span />;
  }

  return (
    <Tooltip title={ownerName}>
      <AriaButton
        onClick={handleOwnerClick}
        onKeyDown={containment.onKeyDown}
        onPointerDown={containment.onPointerDown}
        className='group/owner flex items-center gap-1.5 min-w-0 cursor-pointer pointer-events-auto'
        aria-label={`View ${ownerName}`}
      >
        <span className='relative inline-flex flex-shrink-0'>
          <Avatar size='xxs' src={ownerAvatarUrl} initials={ownerInitials} />
          <span className='absolute inset-0 rounded-full bg-black/0 group-hover/owner:bg-black/10 transition-colors pointer-events-none' />
        </span>
        <span className='text-xs text-[var(--color-text-secondary)] truncate'>
          {ownerName}
        </span>
      </AriaButton>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// CardOwnerPicker
// ---------------------------------------------------------------------------

export function CardOwnerPicker({
  item,
  owner,
  associationTypeName,
  associationType,
}: CardOwnerPickerProps) {
  const { currentOrg } = useOrg();
  const { user } = useAuthInfo();
  const { hasPermission } = usePermissions();
  const orgId = currentOrg?.id;
  const userId = user?.id;

  const entityType = item.entity_type_name;
  const entityTypeConfig = useMemo(
    () => getEntityTypeConfig(entityType),
    [entityType]
  );

  // Reassigning an owner is a remove-plus-add — require both event
  // permissions, mirror `canUpdateAssociations`
  // (crm-object-list-page.tsx:331-339), and additionally require the
  // schema to allow user writes at all. This surface had no permission
  // gate before; a missing/loading `associationType` fails closed.
  const canReassignOwner =
    !!associationTypeName &&
    !entityTypeConfig.readOnly &&
    hasPermission(entityType, 'ahoy_association_added') &&
    hasPermission(entityType, 'ahoy_association_removed') &&
    associationType?.value_write_access === 'all';

  // `value_removal_access: 'none'` rejects every removal once a value is
  // set (`association_immutable`) — hide the clear option in that case.
  const hideClearOption = associationType
    ? isAssociationValueFrozen(associationType, !!owner)
    : false;

  const { users, isLoading: isLoadingUsers } = useOrgUsers(
    canReassignOwner ? orgId : undefined
  );

  const assign = useAssignSingleAssociation({
    orgId,
    userId,
    associationTypeName,
  });

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  // Re-opening always shows the fresh unfiltered-but-capped list rather than
  // a stale filter from the last time the picker was open.
  const handleOpenChange = useCallback((open: boolean) => {
    if (open) {
      setQuery('');
    }
    setIsOpen(open);
  }, []);

  // The drag overlay renders raw card content outside this component
  // (PipelineBoard.tsx:966-976), so it won't remount/reset an open picker —
  // close it ourselves the moment a drag starts anywhere on the board.
  useDndMonitor({
    onDragStart: () => setIsOpen(false),
  });

  const visibleUsers = useMemo(() => {
    const trimmedQuery = query.trim().toLowerCase();
    if (!trimmedQuery) {
      return users.slice(0, OWNER_LIST_CAP);
    }
    return users.filter(
      orgUser =>
        orgUser.displayName.toLowerCase().includes(trimmedQuery) ||
        orgUser.email.toLowerCase().includes(trimmedQuery)
    );
  }, [users, query]);

  const containment = useDndContainment();

  const handleAction = useCallback(
    (key: Key) => {
      const targetEntityId = key === UNASSIGNED_KEY ? null : String(key);
      assign(item, targetEntityId).catch((error: unknown) => {
        toast.error(getUserFriendlyEventError(error));
      });
    },
    [assign, item]
  );

  const ownerName = owner ? getOwnerDisplayName(owner) : undefined;
  const ownerAvatarUrl = owner
    ? extractStringProperty(owner, 'ahoy_avatar_url')
    : undefined;
  const ownerInitials = useMemo(
    () => (ownerName ? getAvatarInitials(ownerName) : undefined),
    [ownerName]
  );

  const selectedKeys = useMemo(() => {
    if (owner) {
      return new Set([owner.entity_id]);
    }
    return hideClearOption ? new Set<string>() : new Set([UNASSIGNED_KEY]);
  }, [owner, hideClearOption]);

  if (!canReassignOwner) {
    return owner ? <ReadOnlyOwnerBadge owner={owner} /> : <span />;
  }

  return (
    <Dropdown.Root isOpen={isOpen} onOpenChange={handleOpenChange}>
      {owner && ownerName ? (
        <Tooltip title={`Change owner – ${ownerName}`}>
          <AriaButton
            onClick={containment.onClick}
            onKeyDown={containment.onKeyDown}
            onPointerDown={containment.onPointerDown}
            className='group/owner flex items-center gap-1.5 min-w-0 cursor-pointer pointer-events-auto'
            aria-label={`Change owner – ${ownerName}`}
          >
            <span className='relative inline-flex flex-shrink-0'>
              <Avatar
                size='xxs'
                src={ownerAvatarUrl}
                initials={ownerInitials}
              />
              <span className='absolute inset-0 rounded-full bg-black/0 group-hover/owner:bg-black/10 transition-colors pointer-events-none' />
            </span>
            <span className='text-xs text-[var(--color-text-secondary)] truncate'>
              {ownerName}
            </span>
          </AriaButton>
        </Tooltip>
      ) : (
        <AriaButton
          onClick={containment.onClick}
          onKeyDown={containment.onKeyDown}
          onPointerDown={containment.onPointerDown}
          aria-label='Assign owner'
          className={cx(
            'inline-flex items-center justify-center cursor-pointer pointer-events-auto',
            UNOWNED_HIT_AREA_CLASSES
          )}
        >
          <Avatar size='xxs' />
        </AriaButton>
      )}
      <Dropdown.Popover placement='bottom start'>
        <div className='px-2 pt-2 pb-1.5 border-b border-secondary'>
          <Input
            icon={SearchLg}
            size='sm'
            autoFocus
            aria-label='Search owners'
            placeholder='Search'
            value={query}
            onChange={setQuery}
          />
        </div>
        <Dropdown.Menu
          onAction={handleAction}
          selectedKeys={selectedKeys}
          selectionMode='single'
        >
          {!hideClearOption && (
            <Dropdown.Item
              key={UNASSIGNED_KEY}
              id={UNASSIGNED_KEY}
              textValue='Unassigned'
            >
              <span className='text-fg-tertiary italic'>Unassigned</span>
            </Dropdown.Item>
          )}
          {isLoadingUsers && (
            <Dropdown.Item
              key='__loading__'
              id='__loading__'
              isDisabled
              textValue='Loading…'
            >
              <span className='text-fg-tertiary'>Loading…</span>
            </Dropdown.Item>
          )}
          {visibleUsers.map(orgUser => (
            <Dropdown.Item
              key={orgUser.entityId}
              id={orgUser.entityId}
              textValue={orgUser.displayName}
            >
              <span className='flex items-center gap-2 min-w-0'>
                <Avatar
                  size='xxs'
                  initials={getAvatarInitials(orgUser.displayName)}
                />
                <span className='truncate'>{orgUser.displayName}</span>
              </span>
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown.Root>
  );
}
